import type { Action, Location } from 'history';
import { ComponentProps, FC, PropsWithChildren, useCallback } from 'react';
import NavigationPrompt from 'react-router-navigation-prompt';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';

interface NavConfirmPromptProps {
  prevent: ComponentProps<typeof NavigationPrompt>['when'];
  message?: string;
  confirmActionText?: string;
}

const NavConfirmPrompt: FC<PropsWithChildren<NavConfirmPromptProps>> = ({
  prevent,
  children,
  message = 'Changes you made will not be saved. Are you sure you want to leave?',
  confirmActionText = 'Leave',
}) => {
  const blockOnRouteChange = useCallback(
    (currentLocation: Location, nextLocation?: Location, action?: Action) => {
      if (currentLocation.pathname === nextLocation?.pathname) {
        // Skip if there is no actual change in routing
        return false;
      } else if (prevent instanceof Function) {
        return prevent(currentLocation, nextLocation, action);
      } else {
        return prevent;
      }
    },
    [prevent],
  );

  return (
    <NavigationPrompt when={blockOnRouteChange}>
      {({ onConfirm, onCancel }) => (
        <ModalWrapper isOpen handleCloseModal={onCancel}>
          <ConfirmationModal
            header={message}
            cancelAction={onCancel}
            confirmAction={onConfirm}
            confirmActionText={confirmActionText}
          >
            {children}
          </ConfirmationModal>
        </ModalWrapper>
      )}
    </NavigationPrompt>
  );
};

export default NavConfirmPrompt;
